import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const VerificationTest = ({ method, onSuccess, onBack }) => {
  const [testCode, setTestCode] = useState('');
  const [error, setError] = useState('');
  const [testing, setTesting] = useState(false);

  const handleTest = async () => {
    if (!testCode || testCode?.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    try {
      setTesting(true);
      setError('');
      
      // In production, verify the code
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (testCode?.match(/^\d{6}$/)) {
        onSuccess();
      } else {
        setError('Invalid verification code');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-foreground mb-2">Test Your Setup</h2>
        <p className="text-muted-foreground">Let's verify your two-factor authentication is working correctly</p>
      </div>

      {/* Instructions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-3 mb-4">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-foreground mb-2">How to Test</h3>
            <ol className="text-sm text-muted-foreground space-y-2">
              {method === 'authenticator' ? (
                <>
                  <li>1. Open your authenticator app</li>
                  <li>2. Find the MyBodyToday entry</li>
                  <li>3. Enter the current 6-digit code below</li>
                </>
              ) : (
                <>
                  <li>1. We'll send a verification code to your phone</li>
                  <li>2. Check your text messages</li>
                  <li>3. Enter the 6-digit code below</li>
                </>
              )}
            </ol>
          </div>
        </div>
      </div>

      {/* Test Input */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-foreground mb-4">Enter Verification Code</h3>
        <Input
          type="text"
          placeholder="000000"
          value={testCode}
          onChange={(e) => {
            setTestCode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6));
            setError('');
          }}
          error={error}
          maxLength={6}
          className="text-center text-2xl tracking-widest font-mono"
        />
      </div>

      {/* Troubleshooting */}
      <div className="bg-muted rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-2">Troubleshooting</h3>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• Make sure your device's time is set correctly</li>
          <li>• Codes expire after 30 seconds - use the current code</li>
          <li>• If using SMS, check your signal strength</li>
          <li>• Try generating a new code if the current one doesn't work</li>
        </ul>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button variant="outline" onClick={onBack} disabled={testing}>
          Back
        </Button>
        <Button
          onClick={handleTest}
          loading={testing}
          disabled={testCode?.length !== 6}
          className="flex-1"
          iconName="CheckCircle2"
          iconPosition="right"
        >
          Verify Code
        </Button>
      </div>
    </div>
  );
};

export default VerificationTest;